import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link, BookOpen, Plus, Trash2, Search, CheckCircle, X,
  ExternalLink, Copy, Download, HelpCircle, Globe, FileText, Layers,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '../services/client';
import { normalizeCitation, type UICitation, formatCitationAPA, siteNameFromUrl } from './citationUtils';

interface Citation extends UICitation {}

interface WorkspaceTab {
  id: string;
  title?: string;
  url?: string;
}

interface CitationForm {
  title: string;
  url: string;
  author: string;
  sourceType: 'url' | 'tab' | 'document' | 'article';
  quote: string;
  sourceId: string;
}

const EMPTY_FORM: CitationForm = {
  title: '',
  url: '',
  author: '',
  sourceType: 'url',
  quote: '',
  sourceId: '',
};

const SOURCE_TYPES = [
  { id: 'url', label: 'Website', icon: Globe },
  { id: 'article', label: 'Article / Paper', icon: FileText },
  { id: 'tab', label: 'Browser Tab', icon: Layers },
  { id: 'document', label: 'Document', icon: BookOpen },
] as const;

const CitationTrackingSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'recent' | 'unused'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [citations, setCitations] = useState<Citation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [form, setForm] = useState<CitationForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceTabs, setWorkspaceTabs] = useState<WorkspaceTab[]>([]);
  const [copyFeedbackId, setCopyFeedbackId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const loadCitations = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/citations');
      const rows = r.data?.citations ?? r.data ?? [];
      setCitations(Array.isArray(rows) ? rows.map((row) => normalizeCitation(row as Record<string, unknown>)) : []);
    } catch (err) {
      console.error(err);
      setCitations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCitations();
  }, [loadCitations]);

  const loadWorkspaceTabs = useCallback(async () => {
    try {
      const res = await api.get('/sessions');
      const sessions = Array.isArray(res.data) ? res.data : (res.data?.sessions ?? []);
      const active =
        sessions.find((s: { is_active?: boolean; active?: boolean }) => s.is_active || s.active) ??
        sessions[0];
      if (!active) {
        setWorkspaceTabs([]);
        return;
      }
      const tabsRes = await api.get(`/sessions/${active.id}/tabs`);
      const tabs = tabsRes.data?.tabs ?? tabsRes.data ?? [];
      setWorkspaceTabs(
        Array.isArray(tabs)
          ? tabs.slice(0, 12).map((t: WorkspaceTab) => ({
              id: String(t.id),
              title: t.title,
              url: t.url,
            }))
          : [],
      );
    } catch {
      setWorkspaceTabs([]);
    }
  }, []);

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
    loadWorkspaceTabs();
  };

  const importTab = (tab: WorkspaceTab) => {
    setForm({
      title: tab.title || tab.url || 'Untitled tab',
      url: tab.url || '',
      author: '',
      sourceType: 'tab',
      quote: '',
      sourceId: tab.id,
    });
  };

  const submitCitation = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/citations', {
        title: form.title.trim(),
        source_title: form.title.trim(),
        url: form.url.trim(),
        source_url: form.url.trim(),
        source: form.author.trim() || form.sourceType,
        context: form.author.trim() || form.sourceType,
        author: form.author.trim(),
        source_type: form.sourceType,
        source_id: form.sourceId || form.url.trim(),
        quote_text: form.quote.trim(),
      });
      const newCitation = normalizeCitation((res.data?.citation ?? res.data) as Record<string, unknown>);
      setCitations((prev) => [newCitation, ...prev]);
      setShowModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save citation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCitation = async (id: string) => {
    try {
      await api.delete(`/citations/${id}`);
      setCitations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const exportBibliography = async () => {
    if (citations.length === 0) return;
    setExporting(true);
    try {
      const res = await api.post('/citations/bibliography', {
        citation_ids: citations.map((c) => c.id),
        format: 'markdown',
      });
      const text = String(res.data?.bibliography ?? '');
      const blob = new Blob([text], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'khiimori-bibliography.md';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const copyCitation = async (citation: Citation) => {
    const text = formatCitationAPA(citation);
    await navigator.clipboard.writeText(text);
    setCopyFeedbackId(citation.id);
    window.setTimeout(() => setCopyFeedbackId(null), 2000);
  };

  const filteredCitations = citations.filter((c) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      c.title.toLowerCase().includes(q) ||
      c.source.toLowerCase().includes(q) ||
      c.url.toLowerCase().includes(q);
    if (activeTab === 'unused') return matchesSearch && c.usedIn.length === 0;
    if (activeTab === 'recent') return matchesSearch && Date.now() - c.lastUsed.getTime() < 604800000;
    return matchesSearch;
  });

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getUsageInfo = (count: number) => {
    if (count === 0) return { text: 'Not linked yet', badge: 'bg-error-500/20 text-error-400' };
    if (count < 3) return { text: `${count} link${count === 1 ? '' : 's'}`, badge: 'bg-warning-500/20 text-warning-400' };
    return { text: `${count} links`, badge: 'bg-success-500/20 text-success-400' };
  };

  return (
    <PageLayout
      title="Citation Tracking"
      description="Save sources, track where they're used, and export bibliographies"
      icon={Link}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Citations', value: citations.length, textClass: 'text-warning-500' },
          { label: 'Linked', value: citations.filter((c) => c.usedIn.length > 0).length, textClass: 'text-success-500' },
          { label: 'Unlinked', value: citations.filter((c) => c.usedIn.length === 0).length, textClass: 'text-error-400' },
          { label: 'This Week', value: citations.filter((c) => Date.now() - c.dateAdded.getTime() < 604800000).length, textClass: 'text-primary-400' },
        ].map((stat, index) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}>
            <Card hover>
              <div className="text-center">
                <div className={`text-3xl font-bold ${stat.textClass}`}>{stat.value}</div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <Card className="mb-4 border-warning-500/20 bg-warning-500/5">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <HelpCircle className="h-5 w-5 text-warning-400" />
            <div>
              <h3 className="font-semibold text-khiimori-text-primary">How citations work</h3>
              <p className="text-sm text-khiimori-text-muted">Save sources → link to outputs → export bibliography</p>
            </div>
          </div>
          <span className="text-xs text-warning-400">{showHelp ? 'Hide' : 'Show'}</span>
        </button>
        {showHelp && (
          <div className="mt-4 pt-4 border-t border-khiimori-border/10 grid md:grid-cols-3 gap-4 text-sm text-khiimori-text-secondary">
            <div>
              <p className="font-medium text-khiimori-text-primary mb-1">1. Save a source</p>
              <p>Add papers, articles, or synced browser tabs to your citation library with title, URL, and optional quote.</p>
            </div>
            <div>
              <p className="font-medium text-khiimori-text-primary mb-1">2. Automatic linking</p>
              <p>When you generate summaries, GenTabs apps, or research exports, Khiimori links those outputs back to the sources they came from.</p>
            </div>
            <div>
              <p className="font-medium text-khiimori-text-primary mb-1">3. Export & cite</p>
              <p>Export an APA-style Markdown bibliography for papers and reports, or copy individual citations in APA format with one click.</p>
            </div>
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-khiimori-text-muted" />
            <input
              type="text"
              placeholder="Search citations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-khiimori-bg-secondary rounded-xl border border-khiimori-border focus:border-warning-500 focus:ring-2 focus:ring-warning-500/20 outline-none text-khiimori-text-primary"
            />
          </div>
          <button
            type="button"
            onClick={exportBibliography}
            disabled={citations.length === 0 || exporting}
            className="flex items-center gap-2 px-4 py-3 rounded-xl border border-khiimori-border text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export bibliography'}
          </button>
          <button
            type="button"
            onClick={openAddModal}
            className="flex items-center gap-2 px-6 py-3 bg-warning-500 text-khiimori-text-primary rounded-xl font-medium hover:bg-warning-600 transition-all shadow-lg"
          >
            <Plus className="h-5 w-5" />
            Add Citation
          </button>
        </div>
      </Card>

      <div className="flex space-x-2 mb-6">
        {(['all', 'recent', 'unused'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === tab
                ? 'bg-warning-500 text-white shadow-lg'
                : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-khiimori-text-muted py-8">Loading citations…</div>}
      {!loading && filteredCitations.length === 0 && (
        <Card className="text-center py-12">
          <BookOpen className="h-12 w-12 text-khiimori-text-muted mx-auto mb-3" />
          <p className="text-khiimori-text-primary font-medium mb-1">No citations yet</p>
          <p className="text-sm text-khiimori-text-muted mb-4">Save your first source to start tracking provenance.</p>
          <button type="button" onClick={openAddModal} className="inline-flex items-center gap-2 px-4 py-2 bg-warning-500 rounded-lg text-sm font-medium">
            <Plus className="h-4 w-4" />
            Add your first citation
          </button>
        </Card>
      )}

      <div className="space-y-4">
        {filteredCitations.map((citation, index) => {
          const usage = getUsageInfo(citation.usedIn.length);
          return (
            <motion.div key={citation.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
              <Card hover>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="p-3 bg-warning-500 rounded-xl shrink-0">
                      <BookOpen className="h-6 w-6 text-khiimori-text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-khiimori-text-primary mb-1 truncate">{citation.title}</h3>
                      {(citation.author || siteNameFromUrl(citation.url)) && (
                        <p className="text-xs text-khiimori-text-muted mb-1">
                          {[citation.author, siteNameFromUrl(citation.url)].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {citation.url && (
                        <a
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1 truncate mb-2"
                        >
                          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          {citation.url}
                        </a>
                      )}
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded text-xs bg-khiimori-bg-elevated/20 text-khiimori-text-muted capitalize">
                          {citation.source}
                        </span>
                        {citation.usedIn.map((use) => (
                          <span key={use} className="px-2 py-0.5 rounded text-xs bg-success-500/20 text-success-400">
                            Used in {use}
                          </span>
                        ))}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${usage.badge}`}>{usage.text}</span>
                      </div>
                      {citation.note && (
                        <blockquote className="text-sm text-khiimori-text-secondary border-l-2 border-warning-500/40 pl-3 italic">
                          {citation.note}
                        </blockquote>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-xs text-khiimori-text-muted">Added {formatDate(citation.dateAdded)}</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => copyCitation(citation)}
                        className="p-2 hover:bg-khiimori-bg-elevated rounded-lg text-khiimori-text-muted"
                        title="Copy APA citation"
                      >
                        {copyFeedbackId === citation.id ? (
                          <CheckCircle className="h-4 w-4 text-success-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button type="button" onClick={() => handleDeleteCitation(citation.id)} className="p-2 hover:bg-khiimori-bg-elevated rounded-lg text-error-400" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
            onClick={() => !saving && setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="bg-khiimori-bg-elevated rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-khiimori-border shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-khiimori-border">
                <h2 className="text-xl font-bold text-khiimori-text-primary">Add Citation</h2>
                <button type="button" onClick={() => setShowModal(false)} className="p-2 hover:bg-khiimori-bg-tertiary rounded-lg">
                  <X className="h-5 w-5 text-khiimori-text-muted" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {workspaceTabs.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Import from workspace</label>
                    <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                      {workspaceTabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => importTab(tab)}
                          className="px-3 py-1.5 text-xs rounded-lg border border-khiimori-border hover:border-warning-500 text-khiimori-text-secondary truncate max-w-full"
                        >
                          {tab.title || tab.url || 'Tab'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Source type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {SOURCE_TYPES.map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, sourceType: id }))}
                        className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${
                          form.sourceType === id
                            ? 'border-warning-500 bg-warning-500/10 text-warning-400'
                            : 'border-khiimori-border text-khiimori-text-secondary'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">Title *</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Impact of AI on clinical decision-making"
                    className="w-full px-4 py-3 rounded-xl border border-khiimori-border bg-khiimori-bg-secondary text-khiimori-text-primary outline-none focus:border-warning-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">URL</label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-4 py-3 rounded-xl border border-khiimori-border bg-khiimori-bg-secondary text-khiimori-text-primary outline-none focus:border-warning-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">Author / Publisher</label>
                  <input
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                    placeholder="e.g. ScienceDirect, Smith et al."
                    className="w-full px-4 py-3 rounded-xl border border-khiimori-border bg-khiimori-bg-secondary text-khiimori-text-primary outline-none focus:border-warning-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">Quote or note (optional)</label>
                  <textarea
                    value={form.quote}
                    onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                    rows={3}
                    placeholder="Key finding or excerpt you want to remember…"
                    className="w-full px-4 py-3 rounded-xl border border-khiimori-border bg-khiimori-bg-secondary text-khiimori-text-primary outline-none focus:border-warning-500 resize-none"
                  />
                </div>

                {error && <p className="text-sm text-error-400">{error}</p>}
              </div>

              <div className="p-6 border-t border-khiimori-border flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} disabled={saving} className="px-4 py-2 rounded-lg text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitCitation}
                  disabled={saving}
                  className="px-5 py-2 bg-warning-500 rounded-lg font-medium text-khiimori-text-primary hover:bg-warning-600 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save citation'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
        <Card className="bg-gradient-glass border-warning-400/30">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-warning-500 rounded-xl">
              <CheckCircle className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">Provenance tracking</h3>
              <p className="text-sm text-khiimori-text-secondary">
                Citations saved here are linked automatically when you synthesize tabs, generate apps, or export research.
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default CitationTrackingSystem;
